import { useState, useEffect, useMemo } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCut,
  IconFlame,
  IconSend,
  IconSparkles,
  IconWand,
  IconMail,
} from "@tabler/icons-react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { useListStore } from "../../../../../stores/ListStore";
import {
  filterActionNodes,
  getWorkflowBranches,
  getWorkflowBranchesNew,
} from "../../../../../functions/utilFunctions";
import EmailPreviewer from "../../../../../components/View/EmailPreviewer";
import SmvPreviewer from "../../../../../components/View/SmvPreviewer";
import MessageVocalPreviewer from "../../../../../components/View/MessageVocalPreviewer";
import MessagePreviewer from "../../../../../components/View/MessagePreviewer";
import { getFirstPageContacts } from "../../../../../api/ContactAPI";
import { getNodeDetails } from "../flowHorizontal/flowSettings";
import LinkedinAccountSelector from "../../../../../components/Selectors/LinkedinAccountSelector";
import EmailAccountSelector from "../../../../../components/Selectors/EmailAccountSelector";

const PreviewModal = ({ clickedNode }) => {
  const lang = useLanguage((s) => s.language);
  const nodes = useFlowStore((s) => s.nodes);
  const programData = useCampaignStore((s) => s.programData);
  const [contact, setContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const updateNodeData = useFlowStore((s) => s.updateNodeData);

  const branchedActionNodes = useMemo(() => {
    const isMultipleBranch = !!nodes.find((x) => x.data.actionType === "dummy");

    if (isMultipleBranch) {
      return getWorkflowBranchesNew(nodes);
    }
    return [filterActionNodes(nodes)];
  }, [nodes]);

  const branchedEventNodes = useMemo(() => {
    if (nodes[0]?.data?.actionType === "dummy") {
      return getWorkflowBranches(nodes, true);
    }
    return [nodes.filter((x) => x?.data?.actionType === "event")];
  }, [nodes]);

  const [selected, setSelected] = useState(null);

  const preview = useMemo(() => {
    if (selected?.data.actionType === "email") {
      return (
        <EmailPreviewer
          key={selected?.id}
          model_id={selected?.data?.model_id}
          iframeHeight={"100%"}
          signatureId={selected?.data?.signature_id}
          contact={contact}
          paperStyle={{
            radius: 12,
            withBorder: true,
            shadow: "none",
            px: "sm",
          }}
          withEditor
          redVariables
        />
      );
    }
    if (selected?.data.actionType === "smv") {
      return <SmvPreviewer model_id={selected?.data?.model_id} />;
    }
    if (selected?.data.actionType === "linkedin_vocal") {
      return <MessageVocalPreviewer model_id={selected?.data?.model_id} />;
    } else {
      return (
        <MessagePreviewer
          model_id={selected?.data?.model_id}
          iframeHeight={"100%"}
          type={selected?.data?.actionType}
          contact={contact}
          paperStyle={{
            radius: 12,
          }}
        />
      );
    }
  }, [selected, contact]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let contactLists = programData?.contactlist_ids;
    if (contactLists?.length > 0) {
      let list = contactLists[0];
      setLoading(true);
      getFirstPageContacts({ id: list, per_page: 5 })
        .then((res) => {
          let results = res.data.results;
          if (results.length > 0) {
            setContact(res.data.results?.[0]);
            setContacts(res.data.results);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [programData?.contactlist_ids]);

  useEffect(() => {
    let contactLists = programData?.contactlist_ids;
    let list = contactLists[0];

    if (branchedEventNodes.length > 1) {
      let branchIndex = branchedActionNodes.findIndex((branch) =>
        branch.some((n) => n.id === selected?.id),
      );
      let eventParent = branchedEventNodes?.[branchIndex]?.[0];

      if (contactLists?.length > 0) {
        setLoading(true);
        getFirstPageContacts({
          id: list,
          per_page: 5,
          filter: eventParent?.data?.filter,
        })
          .then((res) => {
            let results = res.data.results;
            if (results.length > 0) {
              setContact(res.data.results?.[0]);
              setContacts(res.data.results);
            }
          })
          .finally(() => setLoading(false));
      }
    }
  }, [
    programData?.contactlist_ids,
    selected,
    branchedActionNodes,
    branchedEventNodes,
  ]);

  useEffect(() => {
    setTimeout(() => {
      // 1. On cherche le nœud qui correspond à l'ID souhaité
      // .flat() permet de transformer [[noeud1, noeud2], [noeud3]] en [noeud1, noeud2, noeud3]
      const specificNode = branchedActionNodes
        .flat()
        .find((node) => node.id === clickedNode?.id);

      // 2. On sélectionne ce nœud s'il existe, SINON on garde le tout premier nœud par défaut
      setSelected(specificNode || branchedActionNodes[0]?.[0]);
    }, 500);
  }, [branchedActionNodes, clickedNode?.id]);

  const handleSenderChange = (v) => {
    let isPool = v?.number_of_accounts !== undefined;
    const newValue = v?.id ? parseInt(v?.id) : null;
    updateNodeData(selected?.id, {
      [isPool ? "pool_id" : "integration_id"]: newValue,
      [isPool ? "integration_id" : "pool_id"]: null,
    });
  };

  // ==========================================
  // NOUVELLE INTERFACE UTILISATEUR (UI)
  // ==========================================
  return (
    <Stack mx="auto" py="xl" gap="xl">
      <Group align="flex-start" wrap="nowrap" gap={40}>
        {/* 1. SIDEBAR DES BRANCHES & NŒUDS */}
        <Group align="flex-start" gap={32} wrap="nowrap">
          {branchedActionNodes.map((branch, i) => (
            <Stack key={i} align="center" gap="lg" w={64}>
              <Text fw={700} size="lg" c="dark.3">
                B{i + 1}
              </Text>

              <Box pos="relative" w="100%">
                {/* Ligne verticale de connexion */}
                <Box
                  pos="absolute"
                  top={0}
                  bottom={0}
                  left="50%"
                  w={2}
                  bg="gray.2"
                  style={{ transform: "translateX(-50%)", zIndex: 0 }}
                />

                {/* Liste des nœuds (Icônes) */}
                <Stack
                  gap="xl"
                  pos="relative"
                  align="center"
                  style={{ zIndex: 1 }}
                >
                  {branch.map((item) => {
                    const isSelected = selected?.id === item.id;
                    // Mock: getNodeDetails retourne { label, color, icon }
                    const props = getNodeDetails(item.data?.actionType, {
                      size: 24,
                    });

                    return (
                      <Tooltip
                        position="right"
                        key={item.id}
                        label={`#${item.id} ${props.label}`}
                      >
                        <Box p={2} bg={"#fff"}>
                          <ActionIcon
                            size={48}
                            radius="lg"
                            variant={isSelected ? "filled" : "light"}
                            color={props.color}
                            onClick={() => setSelected(item)}
                            style={{
                              border: isSelected ? "none" : "2px solid white",
                              boxShadow: isSelected
                                ? "0 4px 12px rgba(132, 94, 247, 0.3)"
                                : "none",
                            }}
                          >
                            {props.icon}
                          </ActionIcon>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          ))}
        </Group>

        <Stack flex={1} gap="xl">
          <Paper
            withBorder
            radius="xl"
            p="xl"
            style={{ borderColor: "var(--mantine-color-gray-2)" }}
          >
            {/* Header Prévisualisation */}
            <Group wrap="nowrap" mb="xl">
              <ThemeIcon size={48} radius="xl" color="violet.5">
                {getNodeDetails(selected?.data?.actionType)?.icon}
              </ThemeIcon>
              <Box>
                <Text c="violet.5" fw={600} size="sm">
                  {getNodeDetails(selected?.data?.actionType)?.label ||
                    "Modèle"}{" "}
                  {selected?.id || "1"}
                </Text>
                <Group gap={6}>
                  <IconSparkles size={16} color="var(--mantine-color-gray-5)" />
                  <Text fw={600} c="dark.3">
                    Première prise de contact orientée valeur
                  </Text>
                </Group>
              </Box>
            </Group>

            {/* Faux Sélecteurs : Expéditeur & Contact */}
            <Grid gutter="md" mb="xl">
              <Grid.Col span={6}>
                {selected?.data?.actionType === "email" ? (
                  <EmailAccountSelector
                    value={
                      selected?.data?.pool_id || selected?.data?.integration_id
                    }
                    onChange={handleSenderChange}
                    required
                    size={"md"}
                    radius="xl"
                    isPool={!!selected?.data?.pool_id}
                  />
                ) : (
                  <LinkedinAccountSelector
                    value={
                      selected?.data?.pool_id || selected?.data?.integration_id
                    }
                    onChange={handleSenderChange}
                    required={selected?.data?.actionType !== "linkedin_visit"}
                    size={"md"}
                    radius="xl"
                  />
                )}
                {/* <Select
                  radius="xl"
                  size="md"
                  placeholder="Expéditeur : emma@client.eu"
                  data={[]}
                  leftSection={
                    <IconSend size={16} color="var(--mantine-color-gray-5)" />
                  }
                /> */}
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  radius="xl"
                  size="md"
                  placeholder={`Aperçu : ${contact?.first_name || "Julien"} ${contact?.last_name || "Moreau"}`}
                  data={contacts.map((c) => ({
                    value: c.id?.toString(),
                    label: `${c.properties?.find((x) => x.data_field_id === 3)?.value || "Nom"} ${c.properties?.find((x) => x.data_field_id === 4)?.value || "Prénom"} ${selected?.data.actionType === "email" ? `(${c.properties?.find((x) => x.data_field_id === 1)?.value || "email"})` : ""}`,
                  }))}
                  value={contact?.id?.toString()}
                  onChange={(value) => {
                    const selectedContact = contacts.find(
                      (c) => c.id?.toString() === value,
                    );
                    setContact(selectedContact);
                  }}
                  leftSection={
                    <IconWand size={16} color="var(--mantine-color-gray-5)" />
                  }
                />
              </Grid.Col>
            </Grid>

            {/* Contenu réel de l'email (issu de votre hook) */}
            <Box mb="xl">{selected && preview}</Box>

            {/* Boutons d'édition IA */}
            <Group gap="sm">
              <Button
                variant="default"
                radius="xl"
                leftSection={<IconWand size={16} />}
              >
                Réécrire
              </Button>
              <Button
                variant="default"
                radius="xl"
                leftSection={<IconCut size={16} />}
              >
                Raccourcir
              </Button>
              <Button
                variant="default"
                radius="xl"
                leftSection={<IconFlame size={16} />}
              >
                Enrichir
              </Button>
            </Group>
          </Paper>

          {/* CHATBOX IA (Fake) */}
          <Paper
            withBorder
            radius="xl"
            p="xl"
            style={{ borderColor: "var(--mantine-color-gray-2)" }}
          >
            <Group gap="sm" mb="xl">
              <ThemeIcon size={32} radius="xl" color="violet.0" c="violet.5">
                <IconSparkles size={18} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                Discuter avec l'agent
              </Text>
            </Group>

            {/* Messages */}
            <Stack gap="md" mb="xl">
              {/* Message Agent */}
              <Group align="flex-start">
                <Paper
                  bg="gray.0"
                  px="lg"
                  py="sm"
                  radius="xl"
                  style={{ borderBottomLeftRadius: 4 }}
                >
                  <Text size="sm" c="dark.3">
                    J'ai rédigé toute votre séquence. Sélectionnez un message et
                    demandez-moi de l'ajuster : « rends ça plus court », « ton
                    plus consultatif »...
                  </Text>
                </Paper>
              </Group>

              {/* Message User */}
              <Group align="flex-end" justify="flex-end">
                <Paper
                  bg="violet.5"
                  px="lg"
                  py="sm"
                  radius="xl"
                  style={{ borderBottomRightRadius: 4 }}
                >
                  <Text size="sm" c="white">
                    Développe la problématique du prospect
                  </Text>
                </Paper>
              </Group>

              {/* Message Agent */}
              <Group align="flex-start">
                <Paper
                  bg="gray.0"
                  px="lg"
                  py="sm"
                  radius="xl"
                  style={{ borderBottomLeftRadius: 4 }}
                >
                  <Text size="sm" c="dark.3">
                    C'est fait — j'ai régénéré « Email 1 » selon votre demande.
                  </Text>
                </Paper>
              </Group>
            </Stack>

            {/* Input Chat */}
            <TextInput
              placeholder="Ex : mets en avant la problématique précise de chaque prospect."
              radius="xl"
              size="md"
              rightSection={
                <ActionIcon radius="xl" color="violet.5" size={32} mr={4}>
                  <IconSend size={16} />
                </ActionIcon>
              }
              rightSectionWidth={44}
              styles={{
                input: {
                  backgroundColor: "var(--mantine-color-gray-0)",
                  border: "none",
                },
              }}
            />
          </Paper>
        </Stack>
      </Group>
    </Stack>
  );
};

export default PreviewModal;
